import React, { createContext, useContext, useState, useEffect } from 'react'
import { supabase } from '@/lib/supabaseClient'
import { checkIsMockMode } from '@/lib/dataService'

interface AuthContextType {
  user: any
  role: string | null
  loading: boolean
  logout: () => void
  loginMock: (role: 'admin' | 'head_of_department' | 'teacher') => void
  loginMockUser: (userId: string) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const getCookie = (name: string) => {
  if (typeof window === 'undefined') return undefined
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop()?.split(';').shift()
  return undefined
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<any>(null)
  const [role, setRole] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [isMock, setIsMock] = useState(true)

  useEffect(() => {
    const isMockMode = checkIsMockMode()
    setIsMock(isMockMode)

    if (isMockMode) {
      // Mock Mode Authentication
      const mockUserId = getCookie('mock_user_id')
      const mockRole = getCookie('mock_user')
      
      if (mockUserId) {
        // Load custom user from localStorage
        const data = localStorage.getItem('tesscore_mock_data')
        const profiles = data ? JSON.parse(data).profiles : []
        const profile = profiles.find((p: any) => p.id === mockUserId)
        if (profile) {
          setUser(profile)
          setRole(profile.role)
        } else {
          setUser(null)
          setRole(null)
        }
      } else if (mockRole) {
        setUser({
          id: mockRole === 'admin' ? 'admin-uuid' : mockRole === 'head_of_department' ? 'hod-uuid' : 'teacher-uuid',
          email: `${mockRole}@demo.com`,
          first_name: mockRole === 'admin' ? 'สมศักดิ์' : mockRole === 'head_of_department' ? 'วีระเดช' : 'พัชรี',
          last_name: mockRole === 'admin' ? 'แอดมินระบบ' : mockRole === 'head_of_department' ? 'ยอดคำนวณ' : 'อักษรดี',
          status: 'approved',
        })
        setRole(mockRole)
      } else {
        setUser(null)
        setRole(null)
      }
      setLoading(false)
    } else {
      // Production Supabase Mode Authentication
      const checkUser = async () => {
        const { data: { session } } = await supabase.auth.getSession()
        if (session?.user) {
          setUser(session.user)
          
          // Get profile role
          const { data: profile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single()
          
          setUser({
            ...session.user,
            first_name: profile?.first_name || '',
            last_name: profile?.last_name || '',
            department_id: profile?.department_id || null,
            status: profile?.status || null,
          })
          setRole(profile?.role || 'teacher')
        } else {
          setUser(null)
          setRole(null)
        }
        setLoading(false)
      }

      checkUser()

      const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
        if (session?.user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single()
          setUser({
            ...session.user,
            first_name: profile?.first_name || '',
            last_name: profile?.last_name || '',
            department_id: profile?.department_id || null,
            status: profile?.status || null,
          })
          setRole(profile?.role || 'teacher')
        } else {
          setUser(null)
          setRole(null)
        }
        setLoading(false)
      })

      return () => {
        subscription.unsubscribe()
      }
    }
  }, [])

  const logout = () => {
    if (isMock) {
      document.cookie = 'mock_user=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;'
      document.cookie = 'mock_user_id=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;'
      setUser(null)
      setRole(null)
      window.location.href = '/login'
    } else {
      supabase.auth.signOut().then(() => {
        setUser(null)
        setRole(null)
        window.location.href = '/login'
      })
    }
  }

  const loginMock = (selectedRole: 'admin' | 'head_of_department' | 'teacher') => {
    document.cookie = 'mock_user_id=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;'
    document.cookie = `mock_user=${selectedRole}; path=/; max-age=86400; SameSite=Lax`
    setRole(selectedRole)
    setUser({
      id: selectedRole === 'admin' ? 'admin-uuid' : selectedRole === 'head_of_department' ? 'hod-uuid' : 'teacher-uuid',
      email: `${selectedRole}@demo.com`,
      first_name: selectedRole === 'admin' ? 'สมศักดิ์' : selectedRole === 'head_of_department' ? 'วีระเดช' : 'พัชรี',
      last_name: selectedRole === 'admin' ? 'แอดมินระบบ' : selectedRole === 'head_of_department' ? 'ยอดคำนวณ' : 'อักษรดี',
    })
    window.location.href = '/'
  }

  const loginMockUser = (userId: string) => {
    document.cookie = 'mock_user=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT;'
    document.cookie = `mock_user_id=${userId}; path=/; max-age=86400; SameSite=Lax`
    
    // Load details
    const data = localStorage.getItem('tesscore_mock_data')
    const profiles = data ? JSON.parse(data).profiles : []
    const profile = profiles.find((p: any) => p.id === userId)
    if (profile) {
      setUser(profile)
      setRole(profile.role)
    }
    window.location.href = '/'
  }

  return (
    <AuthContext.Provider value={{ user, role, loading, logout, loginMock, loginMockUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
